import { templates } from "@/data/templates";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Globe,
	Lightbulb,
	Palette,
	Package,
	User,
	FileText,
	ArrowRight,
} from "lucide-react";
import type { QuoteTemplate } from "@/types/quote";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
	Globe,
	Lightbulb,
	Palette,
	Package,
	User,
	FileText,
};

const categoryLabels: Record<string, string> = {
	service: "Service",
	consulting: "Consulting",
	creative: "Creative",
	product: "Product",
	custom: "Custom",
};

interface TemplateSelectorProps {
	onSelect: (templateId: string) => void;
}

export function TemplateSelector({ onSelect }: TemplateSelectorProps) {
	return (
		<div className="min-h-screen flex items-center justify-center p-8">
			<div className="max-w-5xl w-full">
				<div className="text-center mb-16">
					<div className="inline-flex items-center gap-2 mb-6">
						<div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
							<FileText className="w-4 h-4 text-white" />
						</div>
						<span className="text-lg font-semibold tracking-tight text-zinc-100">
							QuoteCraft
						</span>
					</div>
					<h1 className="text-4xl font-light tracking-tight text-zinc-100 mb-3">
						Create a new proposal
					</h1>
					<p className="text-zinc-400 text-lg font-light max-w-md mx-auto">
						Choose a template to get started, or begin with a blank canvas.
					</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{templates.map((template) => (
						<TemplateCard
							key={template.id}
							template={template}
							onSelect={onSelect}
						/>
					))}
				</div>
			</div>
		</div>
	);
}

function TemplateCard({
	template,
	onSelect,
}: {
	template: QuoteTemplate;
	onSelect: (id: string) => void;
}) {
	const Icon = iconMap[template.icon] || FileText;

	return (
		<Card
			className="group cursor-pointer border border-zinc-700/50 hover:border-zinc-600 shadow-none hover:shadow-lg hover:shadow-black/20 transition-all duration-300 bg-zinc-800/50"
			onClick={() => onSelect(template.id)}
		>
			<CardContent className="p-6">
				<div className="flex items-start justify-between mb-5">
					<div
						className="w-10 h-10 rounded-xl flex items-center justify-center"
						style={{ backgroundColor: template.accentColor + "20" }}
					>
						<Icon
							className="w-5 h-5"
							style={{ color: template.accentColor }}
						/>
					</div>
					<ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
				</div>
				<h3 className="text-base font-medium text-zinc-100 mb-1">
					{template.name}
				</h3>
				<p className="text-sm text-zinc-400 font-light mb-4 leading-relaxed">
					{template.description}
				</p>
				<div className="flex items-center gap-2">
					<Badge
						variant="secondary"
						className="text-xs font-normal bg-zinc-700/50 text-zinc-400 hover:bg-zinc-700/50"
					>
						{categoryLabels[template.category]}
					</Badge>
					{template.defaultItems.length > 0 && (
						<Badge
							variant="secondary"
							className="text-xs font-normal bg-zinc-700/50 text-zinc-400 hover:bg-zinc-700/50"
						>
							{template.defaultItems.length} items
						</Badge>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
